import React, { useEffect, useRef } from "react"
import { $convertFromMarkdownString } from "@lexical/markdown"
import { InitialConfigType, LexicalComposer } from "@lexical/react/LexicalComposer"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { cn } from "@ui/utils"
import { $createParagraphNode, $getRoot } from "lexical"

import { LEXICAL_NODES, MARKDOWN_TRANSFORMERS } from "@/components/lexical/config"
import LexicalContentEditable from "@/components/lexical/core/content-editable"
import baseStyles from "@/components/lexical/editors/base.module.css"

type MarkdownReadonlyProps = {
  markdown: string
  proseClasses?: string
  className?: string
  styleOverrides?: React.CSSProperties
}

function MarkdownContentPlugin({ markdown }: { markdown: string }) {
  const [editor] = useLexicalComposerContext()
  const lastMarkdownRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (markdown === lastMarkdownRef.current) return
    lastMarkdownRef.current = markdown

    editor.update(() => {
      const root = $getRoot()
      root.clear()
      if (markdown && markdown.trim().length > 0) {
        $convertFromMarkdownString(markdown, MARKDOWN_TRANSFORMERS)
      } else {
        root.append($createParagraphNode())
      }
    })
  }, [editor, markdown])

  return null
}

export function MarkdownReadonly({
  markdown,
  proseClasses = "prose-markdown",
  className,
  styleOverrides,
}: MarkdownReadonlyProps) {
  const initialConfig: InitialConfigType = {
    namespace: "SlantMarkdownReadonly",
    onError: (error) => console.error(error),
    theme: {
      text: {
        underline: baseStyles.underline,
        strikethrough: baseStyles.strikethrough,
        underlineStrikethrough: baseStyles.underlineStrikethrough,
      },
      list: {
        nested: {
          listitem: baseStyles.nestedListItem,
        },
        checklist: baseStyles.checklist,
        listitemChecked: baseStyles.listItemChecked,
        listitemUnchecked: baseStyles.listItemUnchecked,
      },
      table: baseStyles.table,
      tableCell: baseStyles.tableCell,
      tableCellHeader: baseStyles.tableCellHeader,
      tableCellSelected: baseStyles.tableCellSelected,
      tableSelected: baseStyles.tableSelected,
      tableCellResizer: baseStyles.tableCellResizer,
      tableSelection: baseStyles.tableSelection,
      inlineImage: baseStyles.inlineImage,
    },
    nodes: [...LEXICAL_NODES],
    // Initialize from markdown but keep the editor strictly read-only
    editorState: () => $convertFromMarkdownString(markdown || "", MARKDOWN_TRANSFORMERS),
    editable: false,
  }

  return (
    <div className={cn(baseStyles.base, "h-auto", className)} style={styleOverrides}>
      <LexicalComposer initialConfig={initialConfig}>
        <MarkdownContentPlugin markdown={markdown} />
        <RichTextPlugin
          ErrorBoundary={LexicalErrorBoundary}
          contentEditable={
            <div className={baseStyles.editorContainer}>
              <LexicalContentEditable placeholder="" proseClasses={proseClasses} />
            </div>
          }
        />
      </LexicalComposer>
    </div>
  )
}
